import React from "react";
import {observer} from "mobx-react-lite";
import {
    Alert,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
} from "@mui/material";
import {isAxiosError} from "axios";
import moment from "moment";
import {useStores} from "~/stores";
import {axiosErrorToString} from "~/utils/axios.utils";

type WorkflowExecution = {
    name: string
    state: string
    start_time: unknown
    duration: unknown
    argument: string
    result: string
}

const COLUMNS: {key: keyof WorkflowExecution; label: string}[] = [
    {key: "name", label: "Name"},
    {key: "state", label: "State"},
    {key: "start_time", label: "Start time"},
    {key: "duration", label: "Duration"},
    {key: "argument", label: "Argument"},
    {key: "result", label: "Result"},
];

function formatAttribute(value: unknown): string {
    if (value == null || value === "") return "";
    if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
        return String(value);
    }
    return JSON.stringify(value);
}

function formatStartTime(value: unknown): string {
    if (value == null || value === "") return "";
    const parsed = moment(value as moment.MomentInput);
    if (!parsed.isValid()) return formatAttribute(value);
    return parsed.fromNow();
}

function formatDuration(value: unknown): string {
    const seconds = Number(value);
    if (!Number.isFinite(seconds)) return formatAttribute(value);
    return moment.duration(seconds, "seconds").humanize();
}

function warningFromBadRequest(error: unknown): string {
    if (!isAxiosError(error) || error.response?.status !== 400) return "";
    const data = error.response.data;
    if (data && typeof data === "object" && "detail" in data) return String(data.detail);
    return axiosErrorToString(error);
}

export const SupplierNormalizationWorkflows: React.FC = observer(() => {
    const {authStore, bagStore, materializedApi} = useStores();
    const [open, setOpen] = React.useState(false);
    const [executions, setExecutions] = React.useState<WorkflowExecution[]>([]);
    const [listError, setListError] = React.useState("");
    const [startError, setStartError] = React.useState("");
    const [startWarning, setStartWarning] = React.useState("");
    const [starting, setStarting] = React.useState(false);

    const loadExecutions = React.useCallback(() => {
        return materializedApi.http.get<WorkflowExecution[]>("/m_sp_review/workflows/")
            .then(response => {
                setExecutions(response.data);
                setListError("");
            })
            .catch(error => setListError(axiosErrorToString(error)));
    }, [materializedApi]);

    if (!authStore.isMithraStaffGroup) return null;

    const openDialog = () => {
        setOpen(true);
        void loadExecutions();
    };

    const startWorkflow = () => {
        setStartError("");
        setStartWarning("");
        setStarting(true);
        materializedApi.http.post("/m_sp_review/workflows/start/", {databag_id: bagStore.bagId})
            .then(() => loadExecutions())
            .catch(error => {
                const warning = warningFromBadRequest(error);
                if (warning) setStartWarning(warning);
                else setStartError(axiosErrorToString(error));
            })
            .finally(() => setStarting(false));
    };

    return <>
        <Button sx={{my: 1}} variant="outlined" onClick={openDialog}>
            Workflows
        </Button>
        <Dialog open={open} onClose={() => setOpen(false)} maxWidth="lg" fullWidth>
            <DialogTitle>Workflow executions</DialogTitle>
            <DialogContent>
                {startWarning !== "" && <Alert severity="warning" onClose={() => setStartWarning("")}>{startWarning}</Alert>}
                {startError !== "" && <Alert severity="error" onClose={() => setStartError("")}>{startError}</Alert>}
                {listError !== "" && <Alert severity="error" onClose={() => setListError("")}>{listError}</Alert>}
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            {COLUMNS.map(column => <TableCell key={column.key}>{column.label}</TableCell>)}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {executions.length === 0 && <TableRow>
                            <TableCell colSpan={COLUMNS.length}>No executions</TableCell>
                        </TableRow>}
                        {executions.map(execution => <TableRow key={execution.name}>
                            {COLUMNS.map(column => <TableCell
                                key={column.key}
                                sx={{maxWidth: 320, whiteSpace: "pre-wrap", wordBreak: "break-word"}}>
                                {column.key === "start_time"
                                    ? formatStartTime(execution.start_time)
                                    : column.key === "duration"
                                        ? formatDuration(execution.duration)
                                        : formatAttribute(execution[column.key])}
                            </TableCell>)}
                        </TableRow>)}
                    </TableBody>
                </Table>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setOpen(false)}>Close</Button>
                <Button onClick={() => void loadExecutions()}>Refresh</Button>
                <Button
                    variant="contained"
                    disabled={starting || bagStore.bagId === 0}
                    onClick={startWorkflow}>
                    Start workflow
                </Button>
            </DialogActions>
        </Dialog>
    </>;
});
